import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import Card from "../../components/Card";
import Table from "../../components/Table";
import Pagination from "../../components/Pagination";
import { fetchTatListAction } from "../../actions/fetchTatListAction";
import "./index.scss";

// ── Date formatter ─────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return "—";
  const d = new Date(dateString);
  const dd = d.getDate().toString().padStart(2, "0");
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = d.getHours().toString().padStart(2, "0");
  const min = d.getMinutes().toString().padStart(2, "0");
  const ss = d.getSeconds().toString().padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
};

// ── Status badge ───────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  if (!status) return <span className="tat-badge tat-badge--default">—</span>;
  const lower = status.toLowerCase();
  let cls = "tat-badge--default";
  if (lower.includes("approved")) cls = "tat-badge--approved";
  else if (lower.includes("authorizer")) cls = "tat-badge--authorizer";
  else if (lower.includes("vendor")) cls = "tat-badge--vendor";
  else if (lower.includes("not required")) cls = "tat-badge--na";
  return <span className={`tat-badge ${cls}`}>{status}</span>;
};
const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";

  const [date, time] = dateStr.split(" ");
  return `${date} ${time}`;
};

// ── Column definitions ─────────────────────────────────────────────────────────
const TAT_COLUMNS = [
  {
    header: "Entity ID",
    accessor: "entityId",
    render: (row) => (
      <span className="tat-entity-id">{row.entityId || "—"}</span>
    ),
  },
  {
    header: "Entity Name",
    accessor: "entityName",
    render: (row) => (
      <span className="tat-entity-name">{row.entityName || "—"}</span>
    ),
  },
  {
    header: "Link Shared to Vendor",
    accessor: "linkSharedToVendor",
    render: (row) => (
      <span className="tat-date">{formatDate(row.linkSharedToVendor)}</span>
    ),
  },
  {
    header: "Documents Uploaded",
    accessor: "documentsUploadedOn",
    render: (row) => (
      <span className="tat-date">{formatDate(row.documentsUploadedOn)}</span>
    ),
  },
  {
    header: "Deficiency Count",
    accessor: "defCount",
    render: (row) => <span className="tat-date">{row.defCount}</span>,
  },
  {
    header: "Latest Deficiency",
    accessor: "deficientLatest",
    render: (row) => (
      <span className="tat-date">
        {row.defLinkSentOn
          ? new Date(row.defLinkSentOn).toLocaleDateString("en-GB")
          : "-"}
      </span>
    ),
  },
  {
    header: "Verified On",
    accessor: "verifiedOn",
    render: (row) => (
      <span className="tat-date">{formatDate(row.verifiedOn)}</span>
    ),
  },
  {
    header: "Current Status",
    accessor: "currentStatus",
    render: (row) => <StatusBadge status={row.currentStatus} />,
  },
];

// console.log("formatDate(row.defCount) :", formatDate(row.defCount));
// ── Page ───────────────────────────────────────────────────────────────────────
const TatHistory = () => {
  const [tatData, setTatData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const debounceTimer = useRef(null);
  const pageSize = 10;

  const loadTatData = useCallback(
    async (page = 1, search = "") => {
      setLoading(true);
      try {
        const result = await fetchTatListAction({
          searchValue: search,
          pageNo: page,
          pageSize,
        });
        if (result.success) {
          setTatData(result.data || []);
          setTotalCount(result.totalCount || 0);
          setTotalPages(result.totalPages || 1);
        } else {
          toast.error(result.message || "Failed to load TAT history");
          setTatData([]);
          setTotalCount(0);
          setTotalPages(1);
        }
      } catch {
        toast.error("Failed to load TAT history");
        setTatData([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Initial load + page change
  useEffect(() => {
    loadTatData(currentPage, searchQuery);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search — resets to page 1
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setCurrentPage(1);
      loadTatData(1, value);
    }, 400);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
    loadTatData(1, "");
  };

  return (
    <div className="tat-history-page">
      {/* ── Header ── */}
      <div className="tat-history-page__header">
        <div>
          <h1 className="tat-history-page__title">TAT History</h1>
          <p className="tat-history-page__subtitle">
            Track turnaround time for vendor onboarding process
          </p>
        </div>
        {totalCount > 0 && (
          <div className="tat-history-page__count">
            <span className="tat-history-page__count-value">{totalCount}</span>
            <span className="tat-history-page__count-label">
              Total Entities
            </span>
          </div>
        )}
      </div>

      <Card>
        {/* ── Search bar ── */}
        <div className="tat-search-bar">
          <div className="tat-search-bar__input-wrap">
            <svg
              className="tat-search-bar__icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="tat-search-bar__input"
              placeholder="Search by entity name or ID..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button
                className="tat-search-bar__clear"
                onClick={handleClearSearch}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="tat-loading">
            <div className="tat-loading__spinner" />
            <p className="tat-loading__text">Loading TAT history...</p>
          </div>
        ) : tatData.length === 0 ? (
          <div className="tat-empty">
            <div className="tat-empty__icon">📊</div>
            <h3 className="tat-empty__title">
              {searchQuery ? "No Results Found" : "No TAT Data Found"}
            </h3>
            <p className="tat-empty__text">
              {searchQuery
                ? `No records match "${searchQuery}". Try a different search.`
                : "There are no TAT records available at the moment."}
            </p>
          </div>
        ) : (
          <>
            <Table
              columns={TAT_COLUMNS}
              data={tatData}
              showPagination={false}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
              showInfo={true}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default TatHistory;
